import {
  Stack,
  Typography,
  Button,
  useTheme,
  Divider,
  Box,
} from "@mui/material";
import { PrimaryButton } from "@/components/common";
import { ReactNode } from "react";
import { usePostAttendanceContext } from "@/contexts/PostAttendanceContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { MonitorHeart } from "@mui/icons-material";
import { useFormatter } from "@/utils/useFormatter";

interface PostAttendanceCardProps {
  title: string;
  description: string;
  status?: string;
  children?: ReactNode;
  priceInfo?: string;
  onActivate?: () => void;
  onDeactivate?: () => void;
}

export function PostAttendanceCard({
  title,
  description,
  status,
  children,
  priceInfo,
  onActivate,
  onDeactivate,
}: PostAttendanceCardProps) {
  const { contractPostAttendance } = usePostAttendanceContext();
  const { access } = useSessionContext();
  const theme = useTheme();

  return (
    <>
      <Stack
        sx={{
          border: "1px solid",
          borderColor: "neutral700.main",
          borderRadius: "15px",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
          minHeight: "400px",
          paddingTop: 4,
          paddingBottom: 3,
        }}
      >
        {priceInfo && (
          <Stack
            sx={{
              position: "absolute",
              top: "12px",
              left: "0px",
              bgcolor: "secondary800.main",
              color: "white",
              padding: "4px 12px",
              borderTopRightRadius: "15px",
              borderBottomRightRadius: "15px",
              fontSize: "12px",
              fontWeight: "bold",
              zIndex: 1,
            }}
          >
            {priceInfo}
          </Stack>
        )}

        {status && (
          <Stack
            sx={{
              position: "absolute",
              top: "12px",
              right: "0px",
              bgcolor:
                status === "Em desenvolvimento"
                  ? "warning.main"
                  : "success.main",
              color: "white",
              padding: "4px 12px",
              borderTopLeftRadius: "15px",
              borderBottomLeftRadius: "15px",
              fontSize: "12px",
              fontWeight: "bold",
              zIndex: 1,
            }}
          >
            {status}
          </Stack>
        )}

        <Stack
          direction="row"
          width="100%"
          justifyContent="center"
          marginTop="auto"
          paddingTop={2}
        >
          <Typography
            sx={{
              fontSize: "1.25rem",
              fontWeight: "500",
              paddingBottom: 2,
              paddingLeft: 5,
              paddingRight: 5,
              paddingTop: 1,
              alignSelf: "center",
              textAlign: "center",
              position: "relative",
              zIndex: 0,
            }}
          >
            {title}
          </Typography>
        </Stack>

        <Stack flexGrow={1} overflow="auto" maxHeight="330px">
          <Typography
            variant="body1"
            color="neutral700.main"
            sx={{
              paddingBottom: 3,
              paddingLeft: 5,
              paddingRight: 5,
            }}
            align="left"
          >
            {description}
          </Typography>

          {children && (
            <Stack paddingLeft={5} paddingRight={5} paddingBottom={3}>
              {children}
            </Stack>
          )}

          {contractPostAttendance && (
            <Stack paddingLeft={5} paddingRight={5} paddingBottom={3}>
              <Divider sx={{ marginY: 2 }} />
              <Typography
                variant="body1"
                fontWeight="600"
                textAlign="center"
                gutterBottom
              >
                Responsável pela ativação
              </Typography>
              <Box
                sx={{
                  backgroundColor: "neutral100.main",
                  p: 2,
                  borderRadius: 2,
                  mb: 1,
                }}
              >
                <Typography
                  variant="body1"
                  fontWeight="500"
                  sx={{ mb: 1, wordWrap: "break-word", overflow: "hidden" }}
                >
                  <strong>Nome:</strong>{" "}
                  {contractPostAttendance?.extension?.find((e) =>
                    e.url?.includes(
                      "https://fluxmed.com.br/fhir/extension/post_attendance/nome",
                    ),
                  )?.valueString || "-"}
                </Typography>
                <Typography variant="body1" fontWeight="500" sx={{ mb: 1 }}>
                  <strong>CPF:</strong>{" "}
                  {useFormatter?.formatCPF(
                    contractPostAttendance?.extension?.find((e) =>
                      e.url?.includes(
                        "https://fluxmed.com.br/fhir/extension/post_attendance/cpf",
                      ),
                    )?.valueString || "-",
                  )}
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="500"
                  sx={{ wordWrap: "break-word", overflow: "hidden" }}
                >
                  <strong>Email:</strong>{" "}
                  {contractPostAttendance?.extension?.find((e) =>
                    e.url?.includes(
                      "https://fluxmed.com.br/fhir/extension/post_attendance/email",
                    ),
                  )?.valueString || "-"}
                </Typography>
              </Box>
            </Stack>
          )}
        </Stack>

        <Stack
          direction="row"
          width="100%"
          justifyContent="center"
          marginTop="auto"
          paddingTop={2}
        >
          {contractPostAttendance ? (
            <Button
              sx={{
                width: "70%",
                height: "40px",
                backgroundColor: access?.roles?.includes("owner")
                  ? "error700.main"
                  : "neutral300.main",
                color: "neutral0.main",
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: access?.roles?.includes("owner")
                    ? "error700.main"
                    : "neutral300.main",
                },
                textTransform: "none",
                fontSize: theme.typography.subtitle2.fontSize,
                "&:disabled": {
                  color: "neutral600.main",
                },
              }}
              startIcon={<span style={{ color: "white" }}>⚠️</span>}
              disabled={!access?.roles?.includes("owner")}
              onClick={onDeactivate || (() => {})}
            >
              {access?.roles?.includes("owner")
                ? "Cancelar FluxZap"
                : "Somente proprietários"}
            </Button>
          ) : (
            <PrimaryButton
              width="70%"
              height="40px"
              onClick={onActivate || (() => {})}
              startIcon={<MonitorHeart />}
              disabled={!access?.roles?.includes("owner")}
            >
              {access?.roles?.includes("owner")
                ? "Ativar FluxZap"
                : "Somente proprietários"}
            </PrimaryButton>
          )}
        </Stack>
      </Stack>
    </>
  );
}
